import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import GamePrice from './GamePrice';
import API_URL from '../config/api';

// Helper functions for tracking recently viewed games
const getRecentlyViewedGames = () => {
  try {
    const stored = sessionStorage.getItem('nightfall_recently_viewed');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const addToRecentlyViewed = (gameId) => {
  try {
    const recent = getRecentlyViewedGames();
    // Remove if already exists
    const filtered = recent.filter(id => id !== gameId);
    // Add to front
    const updated = [gameId, ...filtered].slice(0, 10); // Keep last 10
    sessionStorage.setItem('nightfall_recently_viewed', JSON.stringify(updated));
  } catch (error) {
    console.error('Error updating recently viewed games:', error);
  }
};

function SimilarGames({ currentGame }) {
  const [similarGames, setSimilarGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Track current game as viewed
    if (currentGame?.id) {
      addToRecentlyViewed(currentGame.id);
    }

    if (!currentGame || !currentGame.tagIds || currentGame.tagIds.length === 0) {
      setLoading(false);
      return;
    }

    const fetchSimilarGames = async () => {
      try {
        const response = await fetch(`${API_URL}/games`);
        if (!response.ok) return;
        
        const allGames = await response.json();
        const recentlyViewed = getRecentlyViewedGames();
        
        // Enhanced similarity algorithm
        const gamesWithSimilarity = allGames
          .filter(game => {
            // Exclude current game and recently viewed games
            return game.id !== currentGame.id && !recentlyViewed.includes(game.id);
          })
          .map(game => {
            // Calculate tag overlap
            const matchingTags = game.tagIds?.filter(tagId => 
              currentGame.tagIds?.includes(tagId)
            ) || [];
            
            // Base similarity score from tag overlap
            const tagScore = matchingTags.length;
            
            // Calculate tag overlap percentage (normalized)
            const currentGameTagCount = currentGame.tagIds?.length || 1;
            const gameTagCount = game.tagIds?.length || 1;
            const overlapPercentage = matchingTags.length / Math.max(currentGameTagCount, gameTagCount);
            
            // Diversity bonus: prefer games from different creators
            const creatorDiversity = game.creatorId !== currentGame.creatorId ? 0.5 : 0;
            
            // Combined score: tag overlap + diversity + normalized overlap
            const similarityScore = tagScore * 2 + overlapPercentage * 3 + creatorDiversity;
            
            return {
              ...game,
              similarityScore,
              tagScore,
              overlapPercentage,
              creatorDiversity
            };
          })
          .filter(game => game.tagScore > 0) // Only games with at least one matching tag
          .sort((a, b) => {
            // Primary sort: similarity score
            if (Math.abs(b.similarityScore - a.similarityScore) > 0.1) {
              return b.similarityScore - a.similarityScore;
            }
            // Secondary sort: prefer different creators for diversity
            if (a.creatorDiversity !== b.creatorDiversity) {
              return b.creatorDiversity - a.creatorDiversity;
            }
            // Tertiary sort: use game order as tiebreaker
            return (a.order || 0) - (b.order || 0);
          });
        
        // Select top 3 with diversity optimization
        const selectedGames = [];
        const usedCreators = new Set();
        
        for (const game of gamesWithSimilarity) {
          if (selectedGames.length >= 3) break;
          
          // Prefer games from different creators, but don't skip if we can't find 3
          if (selectedGames.length < 2 || !usedCreators.has(game.creatorId)) {
            selectedGames.push(game);
            usedCreators.add(game.creatorId);
          }
        }
        
        // If we don't have 3 yet, fill with remaining games
        if (selectedGames.length < 3) {
          for (const game of gamesWithSimilarity) {
            if (selectedGames.length >= 3) break;
            if (!selectedGames.find(g => g.id === game.id)) {
              selectedGames.push(game);
            }
          }
        }
        
        setSimilarGames(selectedGames.slice(0, 3));
      } catch (error) {
        console.error('Error fetching similar games:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSimilarGames();
  }, [currentGame]);

  const handleGameClick = (gameId) => {
    // Track this game as viewed before navigation
    addToRecentlyViewed(gameId);
    
    window.scrollTo(0, 0);
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
    navigate(`/game/${gameId}`);
  };

  if (loading) {
    return null;
  }

  if (similarGames.length === 0) {
    return null;
  }

  return (
    <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl p-6 lg:p-8">
      <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4 lg:mb-6">Similar Games</h2>
      <div className="grid grid-cols-3 gap-3">
        {similarGames.map(game => (
          <div
            key={game.id}
            onClick={() => handleGameClick(game.id)}
            className="flex flex-col bg-dark-bg/30 border border-dark-border rounded-xl hover:bg-dark-bg/50 hover:border-accent-blue/30 transition-all cursor-pointer group overflow-hidden"
          >
            {game.mainImage || game.featuredImage ? (
              <div className="w-full aspect-[16/9] rounded-t-xl overflow-hidden border-b border-dark-border">
                <img
                  src={game.mainImage || game.featuredImage}
                  alt={game.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
            ) : (
              <div className="w-full aspect-[16/9] rounded-t-xl bg-gradient-to-br from-dark-border via-dark-card to-dark-surface border-b border-dark-border flex items-center justify-center">
                <div className="text-white/40 text-xs">No Image</div>
              </div>
            )}
            <div className="p-3 flex flex-col gap-1.5">
              <h3 className="text-white font-semibold text-xs md:text-sm mb-0.5 group-hover:text-accent-blue transition-colors truncate">
                {game.title}
              </h3>
              {game.developer && (
                <p className="text-white/50 text-xs truncate">{game.developer}</p>
              )}
              <div className="mt-auto">
                <GamePrice game={game} size="sm" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default SimilarGames;

